/**
 * @changelog
 *
 * 2026-03-30 (0.1.0) (jhoward)
 *   - Add validateHotelOffer helper for validating hotel offers and handling redirect logic.
 *
 * 2026-04-16 (0.1.1) (jhoward)
 *   - Add fallback to location.href if window.open fails in src\helpers\validateHotelOffer.ts.
 *
 * 2026-10-15 (0.2.0) (jhoward)
 *   - Open the booking tab without the 'noopener' feature and cut the opener by hand. With 'noopener',
 *     window.open() always returns null, so the location.href fallback also ran: every booking opened in both the
 *     new tab and this one.
 */

import type { FetchResponse } from '@framework/defs/framework';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import useAppStore from '@/store';
import { validate } from '@rss/routes/offers/book';

export interface ValidateHotelOfferResponse extends FetchResponse {
  data?: {
    redirect_url?: string;
  };
}

export default async function validateHotelOffer(offer: OpcOffer, redirectOnSuccess: boolean = true): Promise<boolean> {
  const response = await peppermillCustomFetchProxy<ValidateHotelOfferResponse>(
    {},
    {
      route: validate({ offerDomain: offer.CASINO_OFFER_DOMAIN, offerCode: offer.CASINO_OFFER_CODE }),
    },
  );

  if (response.message?.length) {
    const appStore = useAppStore();
    appStore.setNewSnack(15000, response.message, 200 === response.status ? 'check' : 'error');
  }

  if (200 === response.status && response.data?.redirect_url?.length) {
    window.gtag('event', 'offer_book', {
      event_category: 'offer',
      offer_code: offer.CASINO_OFFER_CODE || 'unknown',
      offer_domain: offer.CASINO_OFFER_DOMAIN || 'unknown',
    });
    // appStore.isOverlayActive = true;
    // appStore.isLoadingSpinnerActive = true;
    // appStore.overlayText = 'Redirecting to booking engine...';
    if (redirectOnSuccess) {
      //null only when a popup blocker refused the tab; then take this one instead
      const newWindow = window.open(response.data.redirect_url, '_blank');
      if (newWindow) {
        newWindow.opener = null;
        newWindow.focus();
      } else {
        location.href = response.data.redirect_url;
      }
    }

    return true;
  }

  return false;
}
